import { LoaderCircle, TicketPercent, X } from "lucide-react";
import { useState, type KeyboardEvent } from "react";
import toast from "react-hot-toast";
import { validateCoupon } from "../../services/orders";
import type { AppliedCoupon } from "../../types";

/** Cupom copiado no banner da Home — já aparece preenchido no checkout. */
export const COUPON_STORAGE_KEY = "@NinjaBurger:coupon";

const readSavedCoupon = () => {
  try {
    return sessionStorage.getItem(COUPON_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

interface CouponFieldProps {
  whatsapp: string;
  applied: AppliedCoupon | null;
  onApply: (coupon: AppliedCoupon) => void;
  onRemove: () => void;
}

export const CouponField = ({ whatsapp, applied, onApply, onRemove }: CouponFieldProps) => {
  const [code, setCode] = useState(readSavedCoupon);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed || checking) return;
    // O cupom de primeiro pedido é validado pelo número do cliente.
    if (whatsapp.replace(/\D/g, "").length < 10) {
      setError("Preencha seu WhatsApp antes de aplicar o cupom.");
      return;
    }
    setChecking(true);
    setError(null);
    try {
      const result = await validateCoupon(trimmed, whatsapp);
      if (result.valid) {
        onApply({ code: result.code, percent: result.percent });
        toast.success(`Cupom ${result.code} aplicado: ${result.percent}% OFF`);
      } else {
        setError(result.message);
      }
    } catch {
      setError("Não foi possível validar o cupom agora. Tente de novo.");
    } finally {
      setChecking(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Enter aplica o cupom em vez de enviar o pedido.
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    }
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm">
        <span className="flex items-center gap-2 font-semibold text-success">
          <TicketPercent className="size-5" aria-hidden="true" />
          {applied.code} · {applied.percent}% OFF aplicado
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-muted transition-colors hover:text-white"
        >
          <X className="size-3.5" aria-hidden="true" /> Remover
        </button>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="cupom" className="mb-1.5 block text-xs font-medium text-muted">
        Cupom de desconto
      </label>
      <div className="flex gap-2">
        <input
          id="cupom"
          className="field uppercase placeholder:normal-case"
          placeholder="Tem um cupom?"
          autoComplete="off"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "cupom-erro" : undefined}
        />
        <button
          type="button"
          onClick={apply}
          disabled={!code.trim() || checking}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl border border-line px-5 text-sm font-semibold text-white transition-colors hover:border-primary hover:text-primary disabled:pointer-events-none disabled:opacity-50"
        >
          {checking ? <LoaderCircle className="size-4 animate-spin" aria-label="Validando cupom" /> : "Aplicar"}
        </button>
      </div>
      {error && (
        <p id="cupom-erro" role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
};
